"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, UserCheck, Cpu, GraduationCap, ArrowRight, Sparkles, CircleCheck as CheckCircle2, Clock, ShieldCheck } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
interface StepItem {
  stage: string;
  icon: React.ElementType;
  title: {
    en: string;
    vi: string;
  };
  description: {
    en: string;
    vi: string;
  };
  highlight: {
    en: string;
    vi: string;
  };
}
const STEPS: StepItem[] = [{
  stage: "STAGE 01",
  icon: CalendarCheck,
  title: {
    en: "Book Online",
    vi: "Đăng Ký Trực Tuyến"
  },
  description: {
    en: "Select your preferred age track, convenient center location, and trial class schedule.",
    vi: "Chọn độ tuổi phù hợp, cơ sở đào tạo thuận tiện và khung giờ học thử mong muốn."
  },
  highlight: {
    en: "Takes < 2 Mins",
    vi: "Chỉ mất 2 phút"
  }
}, {
  stage: "STAGE 02",
  icon: UserCheck,
  title: {
    en: "Meet the Advisor",
    vi: "Gặp Cố Vấn Tăng Tốc"
  },
  description: {
    en: "Discuss student learning goals and current tech interest with a robotics engineering advisor.",
    vi: "Trao đổi mục tiêu học tập, sở thích công nghệ và nhận tư vấn từ cố vấn kỹ thuật."
  },
  highlight: {
    en: "1-on-1 Consultation",
    vi: "Tư vấn 1:1 chuyên sâu"
  }
}, {
  stage: "STAGE 03",
  icon: Cpu,
  title: {
    en: "Visit Lab / Trial Class",
    vi: "Tham Quan Lab & Lớp Trải Nghiệm"
  },
  description: {
    en: "Hands-on LEGO & VEX robotics kit assembly and live programming session in our cleanroom lab.",
    vi: "Trực tiếp lắp ráp mô hình LEGO/VEX robotics và lập trình thực hành tại phòng lab."
  },
  highlight: {
    en: "60–90 Mins Practice",
    vi: "60–90 phút thực hành"
  }
}, {
  stage: "STAGE 04",
  icon: GraduationCap,
  title: {
    en: "Enrollment Decision",
    vi: "Quyết Định Nhập Học"
  },
  description: {
    en: "Receive a personalized robotics learning roadmap and tailored course placement recommendation.",
    vi: "Nhận đánh giá năng lực chi tiết, lộ trình học tập tối ưu và tư vấn lớp phù hợp."
  },
  highlight: {
    en: "Tailored Growth Plan",
    vi: "Lộ trình riêng biệt"
  }
}];
export default function AdmissionsProcess() {
  const [lang, setLang] = useState<"en" | "vi">("vi");
  useEffect(() => {
    const getInitialLang = (): "en" | "vi" => {
      if (typeof window === "undefined") return "vi";
      const saved = localStorage.getItem("roboto_lang");
      return saved === "en" || saved === "vi" ? saved : "vi";
    };
    setLang(getInitialLang());
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail && (customEvent.detail === "en" || customEvent.detail === "vi")) {
        setLang(customEvent.detail);
      } else {
        const saved = localStorage.getItem("roboto_lang");
        setLang(saved === "en" || saved === "vi" ? saved : "vi");
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleLangChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleLangChange);
    };
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="admissionsprocess" data-nav="light" className="relative bg-background text-foreground overflow-hidden py-16 md:py-24">
      {/* Decorative Technical Grid Texture */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,var(--border)_1px,transparent_1px),linear-gradient(to_bottom,var(--border)_1px,transparent_1px)] bg-[size:3.5rem_3.5rem] opacity-25 [mask-image:radial-gradient(ellipse_70%_50%_at_50%_50%,#000_70%,transparent_100%)]" aria-hidden="true" />

      {/* Decorative Accent Glow */}
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[36rem] h-[18rem] bg-secondary/15 rounded-full blur-3xl" aria-hidden="true" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          {/* Lime Green precision spec rail accent */}
          <div className="flex items-center gap-2 mb-4">
            <span className="h-0.5 w-6 bg-secondary rounded-full" />
            <Badge variant="outline" className="border-secondary/30 text-secondary bg-secondary/10 px-3 py-1 text-xs font-mono tracking-wider uppercase rounded-full">
              {lang === "en" ? "ADMISSIONS PATHWAY / QUY TRÌNH TUYỂN SINH" : "QUY TRÌNH TUYỂN SINH / ADMISSIONS PATHWAY"}
            </Badge>
            <span className="h-0.5 w-6 bg-secondary rounded-full" />
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif">
            {lang === "en" ? "How to Join ROBOTO Cleanroom Lab" : "Quy Trình Tham Gia Lab ROBOTO"}
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
            {lang === "en" ? "Experience our state-of-the-art robotics learning environment through a structured, hands-on trial session for students aged 4–16." : "Trải nghiệm môi trường học tập robot hiện đại cùng buổi học thử thực hành chuyên sâu dành cho học viên từ 4–16 tuổi."}
          </p>
        </div>

        {/* 4 Step Process Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 relative">
          {STEPS.map((step, index) => {
          const Icon = step.icon;
          const isLast = index === STEPS.length - 1;
          return <motion.div key={step.stage} data-index={index} variants={itemVariants} className="relative flex flex-col h-full">
                {/* Horizontal Sequence Arrow Indicator (Desktop) */}
                {!isLast && <div className="hidden lg:block absolute top-12 -right-4 z-20 translate-x-1/2 -translate-y-1/2 pointer-events-none" aria-hidden="true">
                    <div className="flex items-center justify-center size-8 rounded-full bg-background border border-border shadow-xs text-secondary">
                      <ArrowRight className="size-4" />
                    </div>
                  </div>}

                <Card className="relative flex flex-col justify-between h-full border border-border/80 bg-card/90 backdrop-blur-sm shadow-sm hover:shadow-md hover:border-secondary/50 transition-all duration-300 group overflow-hidden">
                  {/* Top Spec Corner Line */}
                  <div className="absolute top-0 inset-x-0 h-1 bg-muted group-hover:bg-secondary transition-colors duration-300" />

                  <CardContent className="p-6 flex flex-col h-full justify-between gap-6">
                    <div>
                      {/* Stage Badge & Icon Header */}
                      <div className="flex items-center justify-between mb-5">
                        <span className="inline-block text-xs font-mono font-bold tracking-widest text-secondary bg-secondary/10 px-2.5 py-1 rounded-md border border-secondary/20">
                          {step.stage}
                        </span>

                        <div className="size-12 rounded-xl bg-secondary/10 border border-secondary/20 flex items-center justify-center text-secondary group-hover:bg-secondary group-hover:text-secondary-foreground transition-all duration-300">
                          <Icon className="size-6 shrink-0 transition-transform duration-300 group-hover:scale-110" />
                        </div>
                      </div>

                      {/* Step Title */}
                      <h3 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                        {step.title[lang]}
                      </h3>

                      {/* Step Description */}
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.description[lang]}
                      </p>
                    </div>

                    {/* Step Highlight Footer */}
                    <div className="pt-4 border-t border-border/60 flex items-center gap-2 text-xs font-medium text-foreground/80">
                      <CheckCircle2 className="size-4 text-secondary shrink-0" />
                      <span>{step.highlight[lang]}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Visual Lab Showcase & Trial CTA Card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }} className="mt-12 lg:mt-16 rounded-2xl border border-border bg-card p-6 md:p-8 shadow-sm overflow-hidden relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 flex flex-col gap-4">
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-secondary font-semibold">
                <Sparkles className="size-4" />
                <span>
                  {lang === "en" ? "Interactive Equipment Experience" : "Trải Nghiệm Thiết Bị Thực Tế"}
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
                {lang === "en" ? "Ready to build your first robot in class?" : "Sẵn sàng chế tạo robot đầu tiên tại phòng lab?"}
              </h3>

              <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                {lang === "en" ? "Every trial student works directly with genuine LEGO Education & VEX Robotics kits under 1-on-1 guidance from technical instructors." : "Mỗi học viên tham gia học thử đều được thao tác trực tiếp trên bộ thiết bị LEGO Education & VEX Robotics chính hãng dưới sự hướng dẫn 1:1 từ các giảng viên kỹ thuật."}
              </p>

              <div className="flex flex-wrap items-center gap-4 pt-2">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90 font-medium">
                  <Clock className="size-4 text-secondary" />
                  <span>
                    {lang === "en" ? "Flexible schedules" : "Lịch học linh hoạt"}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-foreground/90 font-medium">
                  <ShieldCheck className="size-4 text-secondary" />
                  <span>
                    {lang === "en" ? "No obligation fee" : "Hoàn toàn miễn phí"}
                  </span>
                </div>
              </div>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground font-semibold shadow-md hover:bg-primary/90">
                  <Link to="/FreeTrialContactPage">
                    {lang === "en" ? "Book a Free Trial Class" : "Đăng Ký Học Thử Miễn Phí"}
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg">
                  <Link to="/ProgramsPage">
                    {lang === "en" ? "Explore Programs" : "Xem Chương Trình Học"}
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Photo Illustration Anchor */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-xl overflow-hidden border border-border shadow-md aspect-4/3 group">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_lab_trial.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_lab_trial.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/robotics_lab_trial.png-wvc-sizes" alt={lang === "en" ? "Students building LEGO robotics in ROBOTO Lab" : "Học sinh thực hành lắp ráp robot tại phòng lab ROBOTO"} loading="lazy" className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />

                <div className="absolute bottom-3 left-3 right-3 text-white text-xs bg-black/60 backdrop-blur-md p-2.5 rounded-lg border border-white/10 flex items-center justify-between">
                  <span className="font-mono text-white/90">LAB STATION #04</span>
                  <span className="text-secondary font-semibold">LEGO & VEX Kits</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}